import { useEffect } from 'react';
import { create } from 'zustand';

/**
 * The colour scheme.
 *
 * `tokens.css` has carried a complete light palette since the first commit —
 * defined in full rather than by inverting a few values, because a
 * partially-inverted palette is how contrast bugs ship — and nothing had ever
 * switched to it. This is that switch.
 *
 * Three states, not two. "System" is not a synonym for dark: someone whose OS
 * flips at sunset wants the app to flip with it, and a two-state toggle forces
 * them to choose a side and then change it twice a day.
 */

export type Theme = 'dark' | 'light' | 'system';

const STORAGE_KEY = 'aksolot.theme';

function load(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'dark' || stored === 'light' || stored === 'system') return stored;
  } catch {
    // Private browsing. The default applies; only its memory is lost.
  }
  return 'dark';
}

interface ThemeStore {
  theme: Theme;
  set: (theme: Theme) => void;
}

export const useTheme = create<ThemeStore>((set) => ({
  theme: load(),
  set: (theme) => {
    set({ theme });
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // As above.
    }
  },
}));

/**
 * Applies the choice to the document, and keeps following the OS while the
 * choice is "system".
 *
 * `data-theme` is written rather than removed for the resolved value, because
 * the stylesheet keys off the attribute; leaving it absent would mean "dark"
 * by default and make the system preference unreachable.
 */
export function useApplyTheme(): void {
  const theme = useTheme((s) => s.theme);

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: light)');

    const apply = () => {
      const resolved = theme === 'system' ? (media.matches ? 'light' : 'dark') : theme;
      document.documentElement.dataset['theme'] = resolved;
    };

    apply();
    // Only subscribed while following the system: an explicit choice must not
    // be overwritten when the OS changes at sunset.
    if (theme !== 'system') return;
    media.addEventListener('change', apply);
    return () => media.removeEventListener('change', apply);
  }, [theme]);
}
